/**
 * Black and white, the way a document scanner delivers a receipt. Each pixel is compared with the
 * mean of its neighbourhood (Bradley & Roth's adaptive threshold, on an integral image), so a
 * shadow across the paper or faded thermal print still separates from the paper around it, where
 * one threshold for the whole page would lose one or the other. A pixel far darker than the page
 * as a whole is ink anyway: inside a large black area (a QR code's blocks, a logo) the
 * neighbourhood is dark too, and the comparison alone would hollow it out.
 *
 * Pure, and shared by the phone (lib/scan/photo.ts) and services/ai (lib/documents.ts).
 */

/** How much darker than its surroundings a pixel must be to count as ink. */
const DARKER_BY = 0.12;
/** Below this share of the page's mean brightness a pixel is ink, whatever surrounds it. */
const DARK = 0.5;

export function binarize(
  page: { data: Uint8Array; width: number; height: number },
  options: { window?: number; darkerBy?: number } = {},
): Uint8Array {
  const { data, width, height } = page;
  // About an eighth of the narrow side: several lines of text, smaller than a shadow.
  const window = options.window ?? Math.max(15, Math.round(Math.min(width, height) / 8));
  const keep = 1 - (options.darkerBy ?? DARKER_BY);
  const half = Math.floor(window / 2);

  // integral[(y + 1) * (width + 1) + (x + 1)] = sum of all pixels above and left of (x, y).
  // Uint32 holds 255 x 16.8 M pixels, more than a page ever has (MAX_SIDE caps it).
  const stride = width + 1;
  const integral = new Uint32Array(stride * (height + 1));
  for (let y = 0; y < height; y += 1) {
    let row = 0;
    for (let x = 0; x < width; x += 1) {
      row += data[y * width + x]!;
      integral[(y + 1) * stride + x + 1] = integral[y * stride + x + 1]! + row;
    }
  }

  const dark = (integral[height * stride + width]! / (width * height)) * DARK;
  const out = new Uint8Array(width * height);
  for (let y = 0; y < height; y += 1) {
    const y0 = Math.max(0, y - half);
    const y1 = Math.min(height - 1, y + half);
    for (let x = 0; x < width; x += 1) {
      const x0 = Math.max(0, x - half);
      const x1 = Math.min(width - 1, x + half);
      const count = (x1 - x0 + 1) * (y1 - y0 + 1);
      const sum =
        integral[(y1 + 1) * stride + x1 + 1]! -
        integral[y0 * stride + x1 + 1]! -
        integral[(y1 + 1) * stride + x0]! +
        integral[y0 * stride + x0]!;
      const value = data[y * width + x]!;
      out[y * width + x] = value < dark || value * count <= sum * keep ? 0 : 255;
    }
  }
  return out;
}
